import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import { ScoreRing } from '@/components/ds/ScoreRing';
import { DimensionBars } from '@/components/ds/DimensionBars';
import { FindingRow } from '@/components/ds/FindingRow';
import {
  SUBJECT, SCORE, SCORE_JUSTIFICATION, EXECUTIVE_SUMMARY, DIMENSION_SCORES,
  DIMENSION_SUMMARIES, FINDINGS, SEVERITY_COUNTS, SAMPLE_REPORT_PUBLISHED,
} from '@/pages/sampleReportContent';
import '@/styles/app-states.css';
import '@/styles/landing.css';
import '@/styles/sample-report.css';

/**
 * /sample-report/ — one real audit, published in full, so a prospect can see
 * the actual output before paying for one.
 *
 * ── BUILT FROM THE LANDING VOCABULARY, NOT A NEW ONE ────────────────────────
 *
 * The first cut invented its own card, its own section rhythm and its own
 * closing band, and it read as a wireframe next to the rest of the site. Every
 * surface here is now one the design system already owns:
 *
 *   .uxa-pt-xl          the section rhythm every landing section uses
 *   .uxa-surface-float  the report surface (no border, over a shadow)
 *   .uxa-dark-panel     the emphasis band, with its eyebrow and .uxa-dim-grid
 *                       — the same block the homepage draws its OWN six
 *                       dimensions in, which is what this section is
 *   .uxa-finding        the finding card, from app-states.css
 *   .uxa-closing        the closing band, AT TOP LEVEL where it can bleed
 *
 * The closing band is the one worth spelling out: it is `padding: 132px` and
 * full-bleed by design. Nesting it inside .uxa-landing-section (max-width
 * 1180, auto margins) both stopped it bleeding AND kept the 264px of vertical
 * padding, which is exactly the acre of dead space the first cut shipped.
 *
 * Light → float → DARK → light → tinted: the same alternation the homepage
 * uses, so the page has a rhythm instead of a uniform field of cards.
 *
 * ── NOINDEX, UNLINKED, AT ITS FINAL URL ─────────────────────────────────────
 *
 * This shipped for review before it shipped for visitors, WITHOUT a /preview/
 * route: /preview/home was removed 2026-08-19 because a live preview route is
 * the page at a second URL and a canonical is a hint, not a directive. Three
 * things held it back — noIndex below, no entry in gen-sitemap.mjs, no inbound
 * link — and cutover was those three reversed, on 2026-08-31, once the record
 * behind the page was a real audit. No route moved, nothing redirects. Depth 1
 * is what made the unlinked state legal: check-hub-links-its-children requires
 * a parent link only at depth 2 or more.
 *
 * ── WHAT THIS PAGE MAY NOT DO ───────────────────────────────────────────────
 *
 * Every number comes from one stored audit record and nothing is written to
 * flatter it. Sourcing, the consent question, why the score is a 50 rather
 * than a 72, and why the six dimension PARAGRAPHS are withheld while their
 * scores are published, are all in sampleReportContent.js, pinned to the
 * fixture by test_sample_report_matches_fixture.py.
 */
export default function SampleReportPage() {
  const { login, user } = useAuth();

  return (
    <div className="uxa-landing">
      <SEO
        title="A real UX audit, published in full"
        // Read from the transcription, not typed. The first cut hard-coded
        // "58 of 100, four verified findings" and both numbers were stale the
        // moment the fixture was replaced -- a meta description is exactly the
        // place a stale figure survives unnoticed, because nothing on the
        // rendered page shows it.
        description={
          `The complete UX audit of roarsinc.com: score ${SCORE.value} of 100, `
          + `${FINDINGS.length} verified findings across six dimensions, with the `
          + 'measurements behind each one.'
        }
        canonical="/sample-report"
        // Driven by the one flag, so the page cannot be indexed while the
        // links that point at it are still held, or vice versa.
        noIndex={!SAMPLE_REPORT_PUBLISHED}
      />

      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <h1 className="uxa-h1">A real UX audit, published in full</h1>
        <p className="uxa-lead uxa-sample-standfirst">
          Not a mockup and not a best case. One stored audit record, reproduced
          without edits: the score it gave, the six dimensions it scored, and
          every finding it raised with the measurements behind them.
        </p>

        {/* PROVENANCE, NOT A TESTIMONIAL. A sample report whose subject is
            unnamed is indistinguishable from an invented one, so the subject,
            the date and the record id are all stated. roarsinc.com is our own
            company, which is why this audit can be published at all. */}
        <p className="uxa-dense uxa-sample-provenance">
          <a
            className="uxa-inline-link"
            href={SUBJECT.url}
            rel="noopener noreferrer nofollow"
            target="_blank"
          >
            {SUBJECT.url}
          </a>
          <span aria-hidden="true"> · </span>our own company
          <span aria-hidden="true"> · </span>audited {SUBJECT.completedAt}
          <span aria-hidden="true"> · </span>record {SUBJECT.auditId}
        </p>
      </section>

      {/* THE SCORE, on the report surface. One block carrying the ring, the
          audit's own justification, the severity counts and the six bars, so
          the headline artefact reads as a report rather than as four
          paragraphs that happen to sit near each other. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-surface-float uxa-sample-report-card">
          <div className="uxa-sample-score">
            <ScoreRing score={SCORE.value} animate={false} />
            <div>
              {/* NO LETTER GRADE. scoring.py's header ends "There are no
                  letter grades", and AuditReportPage:1605 rules that the band
                  label wins over the stored score_label. The record still
                  carried grade/score_label 'D' from August; rendering it would
                  publish a figure the product deleted as if it were current
                  output, on a page whose whole argument is that it is not
                  doing that. The BAND is the live vocabulary, and it comes
                  from the one band table every gauge and chip reads.
                  audit_1512ecaff8a6 carries no grade and no score_label at
                  all, so there is now nothing to leave out; the note stays
                  because the next fixture may carry one again. */}
              <p className="uxa-lead uxa-sample-verdict">
                {SCORE.value} out of 100
                <span aria-hidden="true"> · </span>
                <span className="uxa-sample-band">{SCORE.band}</span>
              </p>
              <p className="uxa-body uxa-sample-justification">{SCORE_JUSTIFICATION}</p>

              {/* The record's own executive_summary, verbatim. The previous
                  fixture's ended mid-word ("'Poste)."), a write-time
                  truncation closed in 3b263df when max_tokens moved 8192 ->
                  16000, and the old transcription refused to publish it for
                  that reason. This one ends on a complete sentence. */}
              <p className="uxa-body uxa-sample-summary">{EXECUTIVE_SUMMARY}</p>

              {/* Counts come from SEVERITY_COUNTS, which is FILTERED. This
                  audit has one Critical finding, so the Critical row does
                  draw. The filter stays because the rule is about the audit
                  that has none: a zero in the full critical treatment would
                  make the loudest thing here a report that there is no
                  problem — ruled 2026-08-19.

                  NOT StatTile: its value renders a p at 30px, off the UI type
                  scale, and this is the first PRERENDERED route to use it (the
                  report page it was built for is behind auth, so the sweep
                  never reaches it). Logged rather than ruled on. */}
              <ul className="uxa-sample-counts">
                {SEVERITY_COUNTS.map(({ tier, count }) => (
                  <li key={tier} data-testid={`sample-severity-${tier}`}>
                    <span className="uxa-sample-counts__n">{count}</span>
                    <span className="uxa-dense">
                      {tier === 'major' ? 'Major' : tier === 'minor' ? 'Minor' : 'Critical'}
                      {count === 1 ? ' finding' : ' findings'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="uxa-sample-bars">
            <DimensionBars scores={DIMENSION_SCORES} testId="sample-dimension-bars" />
          </div>
        </div>
      </section>

      {/* SIX DIMENSIONS in the dark panel — the same block, eyebrow and
          .uxa-dim-grid the homepage draws its own six dimensions in. Same
          content type, same treatment. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-dark-panel">
          <div style={{ textAlign: 'center' }}>
            <span className="uxa-dark-panel__eyebrow">Six dimensions</span>
            <h2
              className="uxa-h2"
              style={{ color: 'var(--text-inverse)', margin: '20px auto 0', maxWidth: 760 }}
            >
              What the audit scored, and why
            </h2>
            <p
              className="uxa-lead"
              style={{ color: 'var(--ink-4)', margin: '16px auto 0', maxWidth: 620 }}
            >
              Navigation, Visual Hierarchy, Friction Points, Accessibility,
              Conversion Design and Mobile UX. These are the audit&rsquo;s own
              scores. Its per-dimension commentary is withheld: three of the six
              paragraphs cite findings the same record&rsquo;s validation step
              dropped, and we do not publish prose that contradicts the findings
              below it.
            </p>
          </div>

          <div className="uxa-dim-grid">
            {DIMENSION_SUMMARIES.map((d) => (
              <div key={d.key} className="uxa-dim-grid__cell">
                <h3
                  className="uxa-h3"
                  style={{ color: 'var(--text-inverse)', margin: '0 0 8px' }}
                >
                  {d.name}
                  <span className="uxa-sample-dim-score">{d.score}</span>
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        <h2 className="uxa-h2">{FINDINGS.length} findings, with the evidence</h2>
        <p className="uxa-lead uxa-sample-section-lead">
          Every finding names the element, the measurement and the fix. Nothing
          below is a general recommendation.
        </p>

        <div className="uxa-sample-findings">
          {FINDINGS.map((f) => (
            <article className="uxa-finding" key={f.id}>
              {/* The chip and its three-step meter come from FindingRow, which
                  wraps SeverityBadge. A second chip implementation here would
                  be a second chance to drop the meter. */}
              <FindingRow
                severity={f.severity}
                title={f.title}
                dimension={f.dimensionName}
                effort={`${f.effort} effort`}
                impact={`${f.impact} impact`}
                testId={`sample-finding-${f.id}`}
              />
              <dl className="uxa-sample-finding__detail">
                <dt className="uxa-dense uxa-sample-finding__term">Who it affects</dt>
                <dd className="uxa-dense">{f.userImpact}</dd>
                <dt className="uxa-dense uxa-sample-finding__term">What to change</dt>
                <dd className="uxa-dense">{f.recommendation}</dd>
              </dl>
            </article>
          ))}
        </div>
      </section>

      {/* TOP LEVEL, not inside .uxa-landing-section. It is padding: 132px and
          full-bleed by design; nesting it constrained the band to 1180px AND
          kept the padding, which is how the first cut shipped 264px of nothing
          above a small button. */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div className="uxa-sample-closing">
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>
            Run this on your own product
          </h2>
          <p
            className="uxa-lead"
            style={{ color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 520 }}
          >
            Same six dimensions, same evidence standard, on a URL you choose.
          </p>
          {/* Same CTA contract as every other primary on the site
              (HomeLanding's closing, UxAuditorPage's hero): signed in goes
              straight to a new audit, signed out opens the login popup. It
              previously linked to /ux-auditor/, which is another marketing
              page — a CTA that leads to more marketing. */}
          <div className="uxa-sample-closing__cta">
            <span className="uxa-sample-closing__knob">
              {user ? (
                <Link
                  to="/audit/new"
                  className="uxa-submit uxa-submit--auto"
                  data-testid="sample-cta"
                >
                  Start an audit
                  <span className="uxa-submit-knob" aria-hidden="true">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={login}
                  className="uxa-submit uxa-submit--auto"
                  data-testid="sample-cta"
                >
                  Start an audit
                  <span className="uxa-submit-knob" aria-hidden="true">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              )}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
